import { z } from 'zod';
import { DateRangeSchema } from './offer-availability.js';

// Shared array-length cap for both batch-reserve (`cells`) and batch-release
// (`reservationIds`). The exact value is an open question left to implementation
// (proposal.md "Open Questions"); mirrors BULK_EDIT_CAP in media-units-bulk-edit.ts
// as the closest existing numeric array-cap precedent in this package.
export const RESERVATION_BATCH_CAP = 500;

/**
 * The shared unresolvable reason for a sold reservation, used verbatim by every
 * fan-out preview (lock, withdrawal, release-rejected, batch-release) — the
 * `action-breakdown` capability requires a sold nośnik be reported the same
 * way on every action, so one exported constant is the single place a future
 * wording change (or i18n key introduction) has to land.
 */
export const SOLD_RESERVATION_UNRESOLVABLE_REASON = 'Reservation has been sold';

/**
 * The shared "do kiedy" deadline (CU-86cbc4t4q), one name (`ttlExpiresAt`) on
 * every bulk reservation entry point — batch-reserve, lock-reservations, and
 * the queue-join request. Accepts either a bare calendar date (`YYYY-MM-DD`,
 * what a date-only picker produces) or a full ISO instant; the two are
 * distinguished by wire FORMAT, never by inferring intent from the parsed
 * value (see `resolveTtlExpiresAt` in `../utils/working-days.js`, the one
 * shared function every creation path MUST use to turn this into an actual
 * instant — a date-only value normalises to end of day Europe/Warsaw, an
 * explicit instant passes through unchanged). The 60-day horizon and
 * "not in the past" checks are enforced server-side against
 * request-processing time, not here — same reasoning as the
 * `targetVisibilityDays`/`ttlExpiresAt` "future" comment below: parse time is
 * not request time.
 */
export const TtlExpiresAtSchema = z.union([
  z.string().date(),
  z.string().datetime({ offset: true }),
]);

// Canonical horizon bound for the shared `ttlExpiresAt` deadline above: how far
// ahead of the request a caller may name a date, in days. Single source of
// truth for the `apps/api` HTTP-level rejection this schema-level type does
// NOT itself enforce (see the comment above — "future"/"beyond horizon" are
// relative to request-processing time, not parse time). Pass this constant as
// `evaluateTtlExpiresAtBound`'s `horizonDays` argument (`../utils/working-days.js`)
// from every entry point that accepts `ttlExpiresAt` — batch-reserve
// (`apps/api/src/reservations/reservations.service.ts`), lock-reservations
// (`apps/api/src/offers/offer-versions/offer-versions.service.ts`), and the
// queue-join request (`apps/api/src/reservations/queue.service.ts`) — instead
// of redeclaring 60.
export const TTL_HORIZON_DAYS = 60;

// The standard queued term used to suggest/seed a default `ttlExpiresAt` on
// the mandatory expiry date pickers: "3 working days from now". Mirrors
// `packages/db/src/queries/reservation-queue/promote-next-queue-entry.ts`'s
// promotion fallback (`addWorkingDays(now, 3)`) — same rule, so the web
// picker's default matches what the server actually applies when a queued
// entry is promoted with no live target expiry. Single source of truth for
// `packages/screens/src/offers/reserve-preliminary/reserve-preliminary-dialog.tsx`,
// `apps/web/src/features/offers/components/unified-select-reserve-confirm-dialog.tsx`,
// and `packages/screens/src/reservations/queue-join/queue-join-panel.tsx` —
// import this instead of redeclaring 3.
export const FALLBACK_SUGGESTION_WORKING_DAYS = 3;

// Canonical upper bound for `targetVisibilityDays` (OPTION visibility window
// length, in days). Single source of truth shared by this schema's `.max()`,
// `apps/api/src/reservations/reservations.service.ts`'s `MAX_OPTION_DAYS`,
// and `apps/web/src/features/offers/components/unified-select-option-dialog.tsx`'s
// day-clamp — keep all three importing this constant instead of redeclaring 365.
export const MAX_OPTION_VISIBILITY_DAYS = 365;

// ─── Batch reserve ─────────────────────────────────────────────

export const BatchReserveCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
});

// Flat base object — no refinements — so `offer-version-lock.ts` can reuse
// `.shape.status` (Zod v4 ZodEffects, the refined type below, has no `.shape`).
export const BatchReserveRequestBaseSchema = z.object({
  offerId: z.number().int().positive(),
  status: z.enum(['SOFT_RESERVATION', 'OPTION']),
  // Batch-level (applies identically to every cell) — not per-cell. Required
  // only when status === 'OPTION', enforced by the superRefine below. Bounds
  // (1-MAX_OPTION_VISIBILITY_DAYS) mirror MAX_OPTION_DAYS / createReservation's
  // validateCreateInput in apps/api/src/reservations/reservations.service.ts.
  targetVisibilityDays: z.number().int().positive().max(MAX_OPTION_VISIBILITY_DAYS).optional(),
  /**
   * REQUIRED shared "do kiedy" deadline (CU-86cbc4t4q), applied identically
   * to every cell — same batch-level semantics as `targetVisibilityDays`.
   * Only takes effect for `status === 'SOFT_RESERVATION'` (createReservation's
   * `buildTtl` never reads it for OPTION, which never carries a TTL). "Must
   * be a future instant" and the 60-day horizon are enforced server-side in
   * `ReservationsService.validateCreateInput`, not here — "future" is
   * relative to request-processing time, not parse time. See
   * `TtlExpiresAtSchema` for the date-only-vs-instant wire shape.
   */
  ttlExpiresAt: TtlExpiresAtSchema,
  cells: z.array(BatchReserveCellSchema).min(1).max(RESERVATION_BATCH_CAP),
});

/**
 * Validated request schema for POST /reservations/batch-reserve.
 * Adds superRefine: targetVisibilityDays is required when status === 'OPTION'.
 * When status === 'SOFT_RESERVATION', targetVisibilityDays is optional and
 * silently ignored downstream — createReservation never reads it for that branch.
 */
export const BatchReserveRequestSchema = BatchReserveRequestBaseSchema.superRefine((val, ctx) => {
  if (val.status === 'OPTION' && val.targetVisibilityDays == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['targetVisibilityDays'],
      message: 'targetVisibilityDays is required when status is OPTION',
    });
  }
});
export type BatchReserveRequest = z.infer<typeof BatchReserveRequestSchema>;

export const BatchReserveResultCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  reservationId: z.number().int().positive(),
  /**
   * The expiry actually assigned to this cell's reservation — a deadline
   * quoted to a client and a deadline the system enforces must be comparable
   * at a glance. Null for an OPTION row, which never carries a TTL (mirrors
   * `BatchReservePreviewCellSchema.ttlExpiresAt`'s own invariant).
   */
  ttlExpiresAt: z.coerce.date().nullable(),
});

// The required-vs-selected day counts for a minimum-reservation-window
// violation (CU-86caypwzm). Named field schemas, not inlined, so any other
// per-item outcome shape needing this exact pair (e.g. campaign-sale.ts's
// `CampaignOutcomeItemSchema` unresolvable member) reuses this declaration
// rather than restating it.
export const MinWindowDaysSchema = z.number().int().positive().optional();
export const SelectedDaysSchema = z.number().int().optional();

export const BatchReserveConflictCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  errorCode: z.string(),
  // Only populated when errorCode === 'MIN_WINDOW_VIOLATION' (CU-86caypwzm) — carries
  // the actual required/selected day counts so the client can show the exact
  // shortfall instead of a generic "period too short" message.
  minWindowDays: MinWindowDaysSchema,
  selectedDays: SelectedDaysSchema,
});

/**
 * A cell that could not be reserved outright but was enrolled in the FCFS
 * waitlist instead. Shared by batch-reserve and lock-reservations
 * (`offer-version-lock.ts`'s `LockReservationsResponseSchema.queued` and
 * `LockPreviewItemSchema`'s `queued` member both import this directly,
 * collapsing what used to be two independently-declared, structurally
 * identical schemas — action-breakdown-remaining-dialogs / "Duplicate
 * outcome cell declarations").
 */
export const BatchReserveQueuedCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  queueEntryId: z.number().int().positive(),
  position: z.number().int().positive(),
});
export type BatchReserveQueuedCell = z.infer<typeof BatchReserveQueuedCellSchema>;

// No `skipped` field on this schema: every validated cell resolves to exactly
// one of `created`/`queued`/`conflicted`. `skipped` is a
// BatchReleaseResponseSchema-only concept (SOLD / not-held / not-found
// classification).
export const BatchReserveResponseSchema = z.object({
  created: z.array(BatchReserveResultCellSchema),
  queued: z.array(BatchReserveQueuedCellSchema),
  conflicted: z.array(BatchReserveConflictCellSchema),
});
export type BatchReserveResponse = z.infer<typeof BatchReserveResponseSchema>;

// ─── Batch reserve TTL preview (CU-86cak92z8) ───────────────────
//
// Read-only preview of the automatically-calculated ttlExpiresAt per cell,
// used by the web creation-confirmation dialog to prefill/blank its date
// picker BEFORE the caller commits to a batch-reserve call. Derived from
// `BatchReserveRequestBaseSchema` via `.pick()` (offerId/status/cells only —
// no targetVisibilityDays/ttlExpiresAt, which this read-only endpoint has no
// use for) so the two shapes can never drift apart; the endpoint it backs
// performs no writes.

export const BatchReservePreviewRequestSchema = BatchReserveRequestBaseSchema.pick({
  offerId: true,
  status: true,
  cells: true,
});
export type BatchReservePreviewRequest = z.infer<typeof BatchReservePreviewRequestSchema>;

export const BatchReservePreviewCellSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  /** Null for OPTION (no TTL) — mirrors `reservations.ttl_expires_at`'s own invariant. */
  ttlExpiresAt: z.coerce.date().nullable(),
});

export const BatchReservePreviewResponseSchema = z.object({
  cells: z.array(BatchReservePreviewCellSchema),
});
export type BatchReservePreviewResponse = z.infer<typeof BatchReservePreviewResponseSchema>;

// ─── Batch release ─────────────────────────────────────────────

/**
 * 'full' releases a reservation entirely, the pre-existing behavior. 'selected'
 * (CU-86cbh03pj) releases only the window the caller's selected cells overlap,
 * shortening or splitting the reservation instead. Defaults to 'full' so a
 * caller that predates this field keeps today's whole-release behavior.
 */
export const BatchReleaseModeSchema = z.enum(['full', 'selected']);
export type BatchReleaseMode = z.infer<typeof BatchReleaseModeSchema>;

/**
 * `reservationIds` alone is NOT constrained to be non-empty: a caller whose
 * selection contains only periods they are QUEUED on (no reservation of
 * their own) still needs to leave those queues through the same action. The
 * cross-field superRefine below requires at least one of the two arrays to
 * be non-empty instead, rejecting only a request that asks for nothing.
 */
const BatchReleaseRequestBaseSchema = z.object({
  reservationIds: z.array(z.number().int().positive()).max(RESERVATION_BATCH_CAP),
  /**
   * The selected unit-and-period pairs (CU-86cbc4t4q) — resolved server-side
   * to the caller's own waiting-queue entries on each period, so a bulk
   * release also leaves those queues. The availability payload carries no
   * queue membership, so the server, not the grid, is what can resolve this.
   */
  cells: z.array(BatchReserveCellSchema).max(RESERVATION_BATCH_CAP),
  mode: BatchReleaseModeSchema.default('full'),
});

export const BatchReleaseRequestSchema = BatchReleaseRequestBaseSchema.superRefine((val, ctx) => {
  if (val.reservationIds.length === 0 && val.cells.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reservationIds'],
      message: 'At least one of reservationIds or cells must be non-empty',
    });
  }
});
export type BatchReleaseRequest = z.infer<typeof BatchReleaseRequestSchema>;

export const BatchReleaseSkippedReasonSchema = z.enum([
  'SOLD_NOT_RELEASABLE',
  'NOT_HELD',
  'NOT_FOUND',
  // mode='selected' only — the resolved window (from the reservation's
  // CURRENT dates, not the caller's stale selection) does not overlap any
  // supplied cell at all.
  'NOT_IN_SELECTED_RANGE',
]);
export type BatchReleaseSkippedReason = z.infer<typeof BatchReleaseSkippedReasonSchema>;

export const BatchReleaseSkippedItemSchema = z.object({
  reservationId: z.number().int().positive(),
  reason: BatchReleaseSkippedReasonSchema,
});

/**
 * Per-id outcome for a mode='selected' release, mirroring the discriminated-
 * by-outcome shape `BatchReleasePreviewItemSchema` already uses. `shortened`
 * carries the released range and the single remaining range; `split` carries
 * the released range, both remaining ranges, and the new reservation's id.
 * Uses `DateRangeSchema` (offer-availability.ts) for the range fields since
 * the spec literally names this shape "DateRange" — an ISO-date-string pair,
 * distinct from this same file's `BatchReserveCellSchema.dateFrom/dateTo`
 * coerced-Date pair, which serves a different (request-cell) purpose.
 */
export const BatchReleaseOutcomeItemSchema = z.discriminatedUnion('outcome', [
  z.object({
    reservationId: z.number().int().positive(),
    outcome: z.literal('released'),
  }),
  z.object({
    reservationId: z.number().int().positive(),
    outcome: z.literal('shortened'),
    releasedRange: DateRangeSchema,
    remainingRange: DateRangeSchema,
  }),
  z.object({
    reservationId: z.number().int().positive(),
    outcome: z.literal('split'),
    releasedRange: DateRangeSchema,
    remainingRanges: z.tuple([DateRangeSchema, DateRangeSchema]),
    newReservationId: z.number().int().positive(),
  }),
]);
export type BatchReleaseOutcomeItem = z.infer<typeof BatchReleaseOutcomeItemSchema>;

/** mode='full' response — unchanged from before the `mode` field existed. */
export const BatchReleaseFullResponseSchema = z.object({
  released: z.array(z.number().int().positive()),
  skipped: z.array(BatchReleaseSkippedItemSchema),
  /** Count of the caller's own waiting-queue entries left as a side effect. */
  queuesLeft: z.number().int().nonnegative(),
});
export type BatchReleaseFullResponse = z.infer<typeof BatchReleaseFullResponseSchema>;

/** mode='selected' response — one discriminated outcome per processed id. */
export const BatchReleaseSelectedResponseSchema = z.object({
  outcomes: z.array(BatchReleaseOutcomeItemSchema),
  skipped: z.array(BatchReleaseSkippedItemSchema),
  queuesLeft: z.number().int().nonnegative(),
});
export type BatchReleaseSelectedResponse = z.infer<typeof BatchReleaseSelectedResponseSchema>;

export const BatchReleaseResponseSchema = z.union([
  BatchReleaseFullResponseSchema,
  BatchReleaseSelectedResponseSchema,
]);
export type BatchReleaseResponse = z.infer<typeof BatchReleaseResponseSchema>;

// ─── Batch release preview (action-breakdown-remaining-dialogs) ────────────
//
// Read-only preview of what a batch release would do to a caller's selected
// nośnik-and-window pairs, resolving server-side which of them the caller
// holds and which they only have a waiting-queue entry on (reservation-engine
// / "Batch release preview"). `cells` only — no `reservationIds` — because
// the preview's whole job is resolving which of the caller's own holds and
// queue entries a selected cell corresponds to, unlike BatchReleaseRequestSchema
// which requires the caller to already know its reservationIds.

export const BatchReleasePreviewRequestSchema = z
  .object({
    cells: z.array(BatchReserveCellSchema).min(1).max(RESERVATION_BATCH_CAP),
    mode: BatchReleaseModeSchema.default('full'),
  })
  .strict();
export type BatchReleasePreviewRequest = z.infer<typeof BatchReleasePreviewRequestSchema>;

/**
 * Per-cell batch-release-preview outcome (reservation-engine / "Batch release
 * preview"). A pair held entirely by another party is neither `releasable_hold`
 * nor `queue_exit` and carries no reason to resolve, so it is simply omitted
 * from the response array rather than modelled as a fourth member.
 */
export const BatchReleasePreviewItemSchema = z.discriminatedUnion('outcome', [
  // `releasedRange` is populated for EVERY `releasable_hold` outcome — the
  // reservation's own current full range for a plain full release (mode='full', or
  // an exact-full-range match under mode='selected'), or the shrunk/split released
  // sub-range for a mode='selected' under-covering selection (mirroring
  // BatchReleaseOutcomeItemSchema's `shortened`/`split` shape, read-only here since
  // the preview creates nothing, so no `newReservationId`). `remainingRange`/
  // `remainingRanges` stay optional — only a mode='selected' under-covering
  // shrink/split leaves anything behind to name. `.optional()` on `releasedRange`
  // itself only guards a reverse-deploy pairing with an old API build that predates
  // this field.
  BatchReserveCellSchema.extend({
    outcome: z.literal('releasable_hold'),
    releasedRange: DateRangeSchema.optional(),
    remainingRange: DateRangeSchema.optional(),
    remainingRanges: z.tuple([DateRangeSchema, DateRangeSchema]).optional(),
  }),
  BatchReserveCellSchema.extend({ outcome: z.literal('queue_exit') }),
  BatchReserveCellSchema.extend({ outcome: z.literal('unresolvable'), reason: z.string() }),
]);
export type BatchReleasePreviewItem = z.infer<typeof BatchReleasePreviewItemSchema>;

export const BatchReleasePreviewResponseSchema = z.array(BatchReleasePreviewItemSchema);
export type BatchReleasePreviewResponse = z.infer<typeof BatchReleasePreviewResponseSchema>;
